import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { asApiError } from '@/services/apiError'

import type {
  AgentSettingsFeatureError,
  ConnectorPermissionView,
  ConnectorPolicyView,
} from '../types'
import { useAgentSettingsLabels } from '../agentSettingsLabels'

export interface AgentContextPolicyPanelProps {
  policy: ConnectorPolicyView | null
  loading: boolean
  error: AgentSettingsFeatureError | null
  onRetry: () => void
}

function PermissionRow({ permission }: { permission: ConnectorPermissionView }) {
  const labels = useAgentSettingsLabels()
  return (
    <TableRow data-testid={`connector-refusal-${permission.key}`}>
      <TableCell className="whitespace-normal font-mono text-xs">
        {permission.key}
      </TableCell>
      <TableCell className="whitespace-normal">
        {permission.operation === null
          ? '—'
          : labels.operation(permission.operation)}
      </TableCell>
      <TableCell className="whitespace-normal">
        {permission.targets.length === 0
          ? '—'
          : permission.targets.map(labels.target).join(', ')}
      </TableCell>
      <TableCell>
        <StatusPill tone={permission.overrideable ? 'review' : 'blocked'}>
          {labels.permissionMode(permission.permissionMode)}
        </StatusPill>
      </TableCell>
      <TableCell
        className="whitespace-normal text-xs text-muted-foreground"
        data-refusal={permission.refusalReason ?? undefined}
      >
        {permission.refusalReason === null
          ? '—'
          : labels.refusal(permission.refusalReason)}
      </TableCell>
    </TableRow>
  )
}

/** Renders only the server's immutable connector classification projection. */
export function AgentContextPolicyPanel({
  policy,
  loading,
  error,
  onRetry,
}: AgentContextPolicyPanelProps) {
  const { t } = useTranslation('agents')
  const labels = useAgentSettingsLabels()

  if (loading)
    return (
      <section className="mt-6" data-testid="agent-context-policy">
        <LoadingPanel label={t('guardrails.context.loading')} />
      </section>
    )
  if (error !== null)
    return (
      <section className="mt-6" data-testid="agent-context-policy">
        <ErrorPanel
          error={asApiError({
            status: error.status,
            key: error.code,
            message: error.message,
          })}
          onRetryClicked={onRetry}
        />
      </section>
    )
  // With a policy loaded, both tables render: the context-source table owns
  // its own empty row, and the refusal table always carries the unknown-tool
  // rule, which is live policy even when nothing else is classified.
  if (policy === null)
    return (
      <section className="mt-6" data-testid="agent-context-policy">
        <EmptyPanel body={t('guardrails.context.empty')} />
      </section>
    )

  return (
    <section className="mt-6" data-testid="agent-context-policy">
      <SectionLabel>{t('guardrails.context.title')}</SectionLabel>
      <div className="mt-2 grid gap-4">
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('guardrails.context.source')}</TableHead>
                <TableHead>{t('guardrails.context.classification')}</TableHead>
                <TableHead>{t('guardrails.context.scope')}</TableHead>
                <TableHead>{t('guardrails.context.overrideability')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policy.tools.length === 0 && (
                <TableRow data-testid="connector-tools-empty">
                  <TableCell
                    colSpan={4}
                    className="whitespace-normal text-sm text-muted-foreground"
                  >
                    {t('guardrails.context.emptyTools')}
                  </TableCell>
                </TableRow>
              )}
              {policy.tools.map((tool) => (
                <TableRow key={tool.toolName} data-testid={`connector-tool-${tool.toolName}`}>
                  <TableCell>
                    <div className="font-mono text-xs">{tool.toolName}</div>
                    <div className="text-xs text-muted-foreground">
                      {tool.connector} · {labels.operation(tool.operation)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill tone={tool.permissionMode === 'refused' ? 'blocked' : 'neutral'}>
                      {labels.permissionMode(tool.permissionMode)}
                    </StatusPill>
                  </TableCell>
                  <TableCell className="text-xs">
                    {tool.targetDependsOnArgs
                      ? t('guardrails.context.runtime')
                      : tool.target === null
                        ? '—'
                        : labels.target(tool.target)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {tool.overrideable
                      ? t('guardrails.context.overrideable')
                      : tool.refusalReason === null
                        ? t('guardrails.context.locked')
                        : labels.refusal(tool.refusalReason)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('guardrails.outputs.key')}</TableHead>
                <TableHead>{t('guardrails.outputs.operation')}</TableHead>
                <TableHead>{t('guardrails.outputs.targets')}</TableHead>
                <TableHead>{t('guardrails.context.classification')}</TableHead>
                <TableHead>{t('guardrails.outputs.reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policy.unconditionalRefusals.map((permission) => (
                <PermissionRow key={permission.key} permission={permission} />
              ))}
              <PermissionRow permission={policy.unknownTool} />
            </TableBody>
          </Table>
        </div>
      </div>
    </section>
  )
}
